import { useMemo } from 'react';
import { useTypedQuery } from 'apiTyped/helpers/useTypedQuery';
import type {
  PriceByVintageParams,
  PriceByVintageSeries,
  VintageDistributionIndicators,
  VintageDistributionParams,
  VintageDistributionSeries
} from 'v2/pages/GlobalView.priceByVintage';

const toGlobalViewQueryParams = (params: PriceByVintageParams) => ({
  network: params.network,
  premium_store: params.premium_store,
  survey_year: params.survey_year,
  period: params.period,
  wine_id: params.wineId ?? undefined,
  competitor_ids: params.competitorIds,
  selected_client_custom_ids: params.selectedClientCustomIds,
  market_id: params.marketId,
  continent_id: params.continentId ?? undefined,
  country_id: params.countryId ?? undefined,
  wine_type_id: params.wineTypeId != null ? [params.wineTypeId] : undefined,
  wine_color_id: params.wineColorId ?? undefined,
  wine_country_id: params.wineCountryId ?? undefined,
  wine_region_id: params.wineRegionId ?? undefined,
  wine_appelation_id: params.wineAppelationId ?? undefined,
  wine_grape_variety_id: params.wineGrapeVarietyId ?? undefined,
  wine_format_group_id: params.wineFormatGroupId ?? undefined,
  store_menu_price_band_id: params.storeMenuPriceBandId ?? undefined,
  store_primary_sub_type_id: params.storePrimarySubTypeId ?? undefined,
  wine_format_size: params.wineFormatSize ?? undefined,
  store_ws_award: params.storeWsAward ?? undefined,
  store_star: params.storeStar ?? undefined,
  store_worlds_best: params.storeWorldsBest ?? undefined,
  store_top_bar: params.storeTopBar ?? undefined,
  store_spirited_award: params.storeSpiritedAward ?? undefined,
  vintage: params.vintage ?? undefined,
  min_price: params.minPrice ?? undefined,
  max_price: params.maxPrice ?? undefined,
  currency: params.currency ?? undefined
});

export const usePriceByVintage = (
  params: PriceByVintageParams,
  enabled: boolean
) => {
  const { isLoading, isError, error, data, refetch } = useTypedQuery(
    'get',
    '/v2/global-view/price-by-vintage',
    {
      params: {
        query: toGlobalViewQueryParams(params)
      },
      queryOpts: { enabled }
    }
  );

  const series: PriceByVintageSeries[] = useMemo(
    () =>
      (data?.series ?? []).map((s) => ({
        entityId: s.entity_id,
        entityName: s.entity_name,
        isOwnBrand: s.is_own_brand ?? false,
        points: s.points.map((p) => ({
          vintage: p.vintage,
          minPrice: p.min_price ?? null,
          medianPrice: p.median_price ?? null,
          maxPrice: p.max_price ?? null,
          facing: p.facing,
          posCount: p.pos_count,
          consumptionRate: p.consumption_rate ?? null
        }))
      })),
    [data]
  );

  return {
    isLoading,
    isError,
    error,
    displayedCurrency: data?.displayed_currency ?? 'USD',
    series,
    refetch
  };
};

export const useVintageDistribution = (
  params: VintageDistributionParams,
  enabled: boolean
) => {
  const { isLoading, isError, error, data, refetch } = useTypedQuery(
    'get',
    '/v2/global-view/vintage-distribution',
    {
      params: { query: toGlobalViewQueryParams(params) },
      queryOpts: { enabled }
    }
  );

  const series: VintageDistributionSeries[] = useMemo(
    () =>
      (data?.series ?? []).map((s) => ({
        entityId: s.entity_id,
        entityName: s.entity_name,
        color: s.color ?? null,
        isOwnBrand: s.is_own_brand ?? false,
        points: s.points.map((p) => ({
          vintage: p.vintage,
          dn: p.dn,
          facing: p.facing,
          sharePct: p.share_pct
        }))
      })),
    [data]
  );

  const indicators: VintageDistributionIndicators = useMemo(
    () => ({
      mostPresentVintage: data?.indicators?.most_present_vintage ?? null,
      ndOlderVintages: data?.indicators?.nd_older_vintages ?? null,
      vintageSpreadYears: data?.indicators?.vintage_spread_years ?? null
    }),
    [data]
  );

  return {
    isLoading,
    isError,
    error,
    series,
    indicators,
    refetch
  };
};
